import React, { useEffect, useState } from 'react';
import { Icon, Button, Input, toast } from '@tviklax/ui-components';
import styled from 'styled-components';
import { API_BASE_URL } from '../../../shared/constants/env';

const getApiOrigin = () => API_BASE_URL.replace(/\/graphql\/?$/, '');

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CardSubtitle = styled.div`
  font-size: 13px;
  color: #64748b;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[4]};
  flex-wrap: wrap;
`;

const SwitchTrack = styled.button<{ $on: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.gray300)};
  position: relative;
  transition: background 0.2s ease-in-out;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${({ $on }) => ($on ? '23px' : '3px')};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: white;
    transition: left 0.2s ease-in-out;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const MaskedFieldRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-wrap: wrap;

  & > div:first-child {
    flex: 1;
    min-width: 220px;
  }
`;

const RevealBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const RevealText = styled.div`
  font-size: 12px;
  color: #92400e;
  font-weight: 600;
`;

const CodeCard = styled.div`
  position: relative;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[4]};
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 12.5px;
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre;
`;

const CodeCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const EndpointRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 13px;
  background: ${({ theme }) => theme.colors.gray100};
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  flex-wrap: wrap;
  word-break: break-all;
`;

const SAMPLE_PAYLOAD = `POST ${getApiOrigin()}/api/v1/orders/external
X-API-Key: tvk_live_••••••••••••••••••••••••••••••••••••
Content-Type: application/json

{
  "counterparty": {
    "name": "Acme Imports LLC",
    "email": "buyer@acme.example",
    "phone": "+1-555-0100",
    "country": "US",
    "address": {
      "street": "123 Market St",
      "city": "San Francisco",
      "state": "CA",
      "postalCode": "94105"
    }
  },
  "currency": "USD",
  "incoterms": "FOB",
  "poNumber": "PO-2026-0456",
  "items": [
    {
      "description": "Cotton T-Shirts",
      "hsCode": "610910",
      "quantity": 500,
      "unitPrice": 4.25,
      "countryOfOrigin": "India"
    }
  ],
  "shipment": {
    "originCountry": "India",
    "destinationCountry": "US",
    "estimatedArrivalDate": "2026-09-15"
  }
}`;

interface Settings {
  enabled: boolean;
  webhookUrl: string | null;
  apiKeyPrefix: string | null;
  hasKey: boolean;
}

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const copyToClipboard = async (value: string, label: string) => {
  try {
    await navigator.clipboard.writeText(value);
    toast({ title: 'Copied', description: `${label} copied to clipboard.` });
  } catch {
    toast({ title: 'Copy failed', description: 'Your browser blocked clipboard access.', variant: 'destructive' });
  }
};

export const ErpIntegration: React.FC = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [webhookInput, setWebhookInput] = useState('');
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${getApiOrigin()}/api/v1/integrations/settings`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load integration settings');
      const data: Settings = await res.json();
      setSettings(data);
      setWebhookInput(data.webhookUrl || '');
    } catch (error) {
      toast({ title: 'Could not load integration settings', description: (error as Error).message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSettings = async (next: { enabled: boolean; webhookUrl: string }) => {
    setSaving(true);
    try {
      const res = await fetch(`${getApiOrigin()}/api/v1/integrations/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(next),
      });
      if (!res.ok) throw new Error('Failed to save integration settings');
      const data: Settings = await res.json();
      setSettings(data);
      toast({ title: 'Saved', description: 'Integration settings updated.' });
    } catch (error) {
      toast({ title: 'Save failed', description: (error as Error).message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = () => {
    if (!settings) return;
    void saveSettings({ enabled: !settings.enabled, webhookUrl: webhookInput });
  };

  const handleSaveWebhook = () => {
    if (!settings) return;
    void saveSettings({ enabled: settings.enabled, webhookUrl: webhookInput });
  };

  const handleRotateKey = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${getApiOrigin()}/api/v1/integrations/api-key/rotate`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed to generate API key');
      const data = await res.json();
      setRevealedKey(data.apiKey);
      setSettings((prev) => (prev ? { ...prev, enabled: true, apiKeyPrefix: data.apiKeyPrefix, hasKey: true } : prev));
      toast({ title: 'API key generated', description: 'Copy it now — it will not be shown again.' });
    } catch (error) {
      toast({ title: 'Could not generate key', description: (error as Error).message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Wrapper>
        <div style={{ color: '#64748b', fontSize: 14 }}>Loading integration settings…</div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Card>
        <ToggleRow>
          <div>
            <CardTitle>Enable Automated API Sync</CardTitle>
            <CardSubtitle>Let an external ERP push orders into Tvikla OS via API key.</CardSubtitle>
          </div>
          <SwitchTrack
            type="button"
            role="switch"
            aria-checked={settings?.enabled || false}
            $on={settings?.enabled || false}
            onClick={handleToggle}
            disabled={saving}
          />
        </ToggleRow>
      </Card>

      <Card>
        <CardTitle><Icon name="Key" size={16} color="#f86400" /> API Token</CardTitle>
        <CardSubtitle>Used as the X-API-Key header when your ERP calls the order-import endpoint.</CardSubtitle>

        {revealedKey && (
          <RevealBanner>
            <RevealText>⚠️ This is shown once — copy it now. You&apos;ll only see the prefix afterwards.</RevealText>
            <MaskedFieldRow>
              <Input value={revealedKey} readOnly />
              <Button variant="outline" size="sm" onClick={() => copyToClipboard(revealedKey, 'API key')}>
                <Icon name="Clipboard" size={14} style={{ marginRight: 6 }} />
                Copy
              </Button>
            </MaskedFieldRow>
          </RevealBanner>
        )}

        <MaskedFieldRow>
          <Input value={settings?.apiKeyPrefix ? `${settings.apiKeyPrefix}••••••••••••••••••••••` : 'No key generated yet'} readOnly />
          <Button variant="outline" size="sm" onClick={handleRotateKey} disabled={saving}>
            {settings?.hasKey ? 'Regenerate Key' : 'Generate Key'}
          </Button>
        </MaskedFieldRow>

        <EndpointRow>
          <Icon name="Globe" size={14} />
          POST {getApiOrigin()}/api/v1/orders/external
          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`${getApiOrigin()}/api/v1/orders/external`, 'Endpoint URL')}>
            <Icon name="Clipboard" size={12} />
          </Button>
        </EndpointRow>
      </Card>

      <Card>
        <CardTitle><Icon name="Send" size={16} color="#f86400" /> Webhook Destination URL</CardTitle>
        <CardSubtitle>Optional — we&apos;ll POST an order-confirmation callback here after an externally-submitted order is created.</CardSubtitle>
        <MaskedFieldRow>
          <Input
            value={webhookInput}
            placeholder="https://your-erp.example.com/webhooks/tvikla-order-confirmed"
            onChange={(e) => setWebhookInput(e.target.value)}
          />
          <Button variant="outline" size="sm" onClick={handleSaveWebhook} disabled={saving}>
            Save
          </Button>
        </MaskedFieldRow>
      </Card>

      <Card>
        <CodeCardHeader>
          <div>
            <CardTitle><Icon name="FileText" size={16} color="#f86400" /> Sample Request</CardTitle>
            <CardSubtitle>Standardized order-import payload your ERP integration should send.</CardSubtitle>
          </div>
          <Button variant="outline" size="sm" onClick={() => copyToClipboard(SAMPLE_PAYLOAD, 'Sample payload')}>
            <Icon name="Clipboard" size={14} style={{ marginRight: 6 }} />
            Copy
          </Button>
        </CodeCardHeader>
        <CodeCard>{SAMPLE_PAYLOAD}</CodeCard>
      </Card>
    </Wrapper>
  );
};
